// pages/analytics/DailyPerformance.jsx — SOUND-410
// Internal analyst dashboard over presentation.mart_daily_performance
// (Snowflake MART_DAILY_PERFORMANCE -> presentation Postgres, SOUND-309).
// Backend: GET /analytics/daily-performance{,/timeseries,/summary,/filters} (SOUND-409).
//
// Grain is (transaction_date × company_id × merchant_name). approval_rate_* are
// percent (0–100); *_vs_prior_day are percentage-point deltas; *_vs_prior_week_pct
// are percent change. Rollup rates are recomputed server-side from base counts.
import React, { useState, useEffect, useMemo } from "react";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth, requireAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import PageHeader from "components/PageLayout";
import PageKpis from "components/PageKpis";
import Skeleton from "components/Skeleton";
import { BarChart3, TrendingUp, DollarSign, CreditCard, CheckCircle, XCircle } from "lucide-react";

// ---------- formatting ----------
const fmtInt = (n) => new Intl.NumberFormat("en-US").format(Number(n || 0));
const fmtCurrency = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(Number(n || 0));
const fmtMoney2 = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(Number(n || 0));
const fmtPct = (n) => (n === null || n === undefined ? "—" : `${Number(n).toFixed(1)}%`);
const fmtPP = (n) => (n === null || n === undefined ? "—" : `${Number(n) >= 0 ? "+" : ""}${Number(n).toFixed(1)} pp`);
const fmtPctChange = (n) =>
  n === null || n === undefined ? "—" : `${Number(n) >= 0 ? "+" : ""}${new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(Number(n))}%`;

function cssVar(name, alpha = 1) {
  if (typeof window === "undefined") return "";
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  if (!raw) return "";
  const [h, s, l] = raw.split(/\s+/);
  return `hsla(${parseFloat(h)}, ${s}, ${l}, ${alpha})`;
}

const RANGE_PRESETS = [
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
  { label: "1y", days: 365 },
];

function isoDaysAgo(n) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
}

function buildQS({ startDate, endDate, companyId, merchantName, limit }) {
  const p = new URLSearchParams();
  if (startDate) p.set("startDate", startDate);
  if (endDate) p.set("endDate", endDate);
  if (companyId) p.set("companyId", companyId);
  if (merchantName) p.set("merchantName", merchantName);
  if (limit) p.set("limit", String(limit));
  return p.toString();
}

const deltaClass = (n) =>
  n > 0 ? "text-green-500" : n < 0 ? "text-destructive" : "text-muted-foreground";

function ChartCard({ title, subtitle, children }) {
  return (
    <div className="surface rounded-xl ring-1 ring-border p-5">
      <div className="mb-3">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      </div>
      <ResponsiveContainer width="100%" height={240}>
        {children}
      </ResponsiveContainer>
    </div>
  );
}

const tooltipStyle = () => ({
  background: cssVar("--card", 0.98),
  border: `1px solid ${cssVar("--border", 1)}`,
  borderRadius: 6,
  fontSize: 12,
});

function DailyPerformanceInner() {
  const { user } = useAuth();
  const effectiveCompanyId = useEffectiveCompanyId();

  const [filters, setFilters] = useState({ companies: [], merchants: [] });
  const [companyId, setCompanyId] = useState("");
  const [merchantName, setMerchantName] = useState("");
  const [rangeDays, setRangeDays] = useState(30);

  const [summary, setSummary] = useState(null);
  const [byCompany, setByCompany] = useState([]);
  const [series, setSeries] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const startDate = useMemo(() => isoDaysAgo(rangeDays), [rangeDays]);
  const endDate = useMemo(() => isoDaysAgo(0), []);

  const auth = useMemo(() => ({ id: user?.id, accessToken: user?.accessToken }), [user]);

  // Load filter options once.
  useEffect(() => {
    if (!user || !effectiveCompanyId) return;
    (async () => {
      try {
        const res = await makeApiRequest("get", apiRoutes.analyticsDailyPerformanceFilters, auth, effectiveCompanyId);
        setFilters(res?.data || { companies: [], merchants: [] });
      } catch {
        /* non-fatal — filters just stay empty */
      }
    })();
  }, [user, effectiveCompanyId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset merchant when company changes (merchant list is company-dependent).
  useEffect(() => { setMerchantName(""); }, [companyId]);

  // Load summary + timeseries + detail rows on scope change.
  useEffect(() => {
    if (!user || !effectiveCompanyId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(false);
      const qs = buildQS({ startDate, endDate, companyId, merchantName });
      const rowQs = buildQS({ startDate, endDate, companyId, merchantName, limit: 2000 });
      try {
        const [s, t, r] = await Promise.all([
          makeApiRequest("get", `${apiRoutes.analyticsDailyPerformanceSummary}?${qs}`, auth, effectiveCompanyId),
          makeApiRequest("get", `${apiRoutes.analyticsDailyPerformanceTimeseries}?${qs}`, auth, effectiveCompanyId),
          makeApiRequest("get", `${apiRoutes.analyticsDailyPerformance}?${rowQs}`, auth, effectiveCompanyId),
        ]);
        if (cancelled) return;
        setSummary(s?.data?.totals || null);
        setByCompany(s?.data?.byCompany || []);
        setSeries((t?.data?.series || []).map((d) => ({ ...d, date: String(d.transaction_date).slice(0, 10) })));
        setRows(r?.data?.data || []);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, effectiveCompanyId, startDate, endDate, companyId, merchantName]); // eslint-disable-line react-hooks/exhaustive-deps

  const merchantOptions = useMemo(
    () => filters.merchants.filter((m) => !companyId || m.companyId === companyId),
    [filters.merchants, companyId]
  );
  const brand = cssVar("--brand-600", 1) || cssVar("--brand", 1) || "#5affa2";
  const blue = "hsl(217 91% 60%)";
  const red = cssVar("--destructive", 1) || "hsl(0 84% 60%)";

  return (
    <div>
      <PageHeader
        title="Daily Performance"
        subtitle="Approval, revenue, and volume across companies and merchants."
        meta={summary ? [
          { label: "Companies", value: fmtInt(summary.companies) },
          { label: "Merchants", value: fmtInt(summary.merchants) },
          { label: "Days", value: fmtInt(summary.days) },
        ] : []}
      />

      <div className="container py-6 space-y-6">
        {/* Controls */}
        <div className="surface rounded-xl ring-1 ring-border p-4 flex flex-col lg:flex-row lg:items-end gap-4">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Company</label>
            <select className="select" value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
              <option value="">All companies</option>
              {filters.companies.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Merchant</label>
            <select className="select max-w-xs" value={merchantName} onChange={(e) => setMerchantName(e.target.value)}>
              <option value="">All merchants</option>
              {merchantOptions.map((m) => <option key={`${m.companyId}|${m.merchantName}`} value={m.merchantName}>{m.merchantName}</option>)}
            </select>
          </div>
          <div className="lg:ml-auto">
            <label className="text-xs text-muted-foreground block mb-1">Range</label>
            <div className="inline-flex rounded-lg ring-1 ring-border overflow-hidden">
              {RANGE_PRESETS.map((p) => (
                <button
                  key={p.days}
                  onClick={() => setRangeDays(p.days)}
                  className={`px-3 py-2 text-sm ${rangeDays === p.days ? "bg-brand/15 text-foreground" : "text-muted-foreground hover:bg-muted/60"}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {error && (
          <div className="surface rounded-xl ring-1 ring-border p-8 text-center text-sm text-destructive">
            Failed to load analytics.
          </div>
        )}

        {/* KPI strip */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="surface rounded-xl ring-1 ring-border p-5">
                <Skeleton.Rect className="h-5 w-20" /><Skeleton.Rect className="h-3 w-12 mt-2" />
              </div>
            ))}
          </div>
        ) : summary && (
          <PageKpis
            columns={{ base: 2, md: 3, lg: 5 }}
            items={[
              { label: "Transactions", value: Number(summary.transactions) || 0, icon: <CreditCard />, tone: "brand" },
              // approval_rate is 0–100; PageKpis "percent" multiplies by 100, so pass the fraction.
              { label: "Approval Rate", value: (Number(summary.approval_rate) || 0) / 100, format: "percent", icon: <CheckCircle />, tone: "success" },
              { label: "Declined", value: Number(summary.declined) || 0, icon: <XCircle />, tone: "destructive" },
              { label: "Revenue", value: Number(summary.revenue) || 0, format: "currency", icon: <DollarSign />, tone: "brand" },
              { label: "Avg Txn Value", value: Number(summary.avg_txn_value) || 0, format: "currency", icon: <TrendingUp />, tone: "info" },
            ]}
          />
        )}

        {/* Charts */}
        {!loading && series.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartCard title="Approval Rate" subtitle="Daily, recomputed from approved / transactions">
              <LineChart data={series} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={cssVar("--border", 0.5)} />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={24} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} width={36} />
                <Tooltip contentStyle={tooltipStyle()} formatter={(v) => [`${Number(v).toFixed(1)}%`, "Approval"]} />
                <Line type="monotone" dataKey="approval_rate" stroke={brand} strokeWidth={2} dot={false} isAnimationActive={false} />
              </LineChart>
            </ChartCard>

            <ChartCard title="Revenue" subtitle="Daily approved revenue (USD)">
              <LineChart data={series} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={cssVar("--border", 0.5)} />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={24} />
                <YAxis tick={{ fontSize: 11 }} width={56} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
                <Tooltip contentStyle={tooltipStyle()} formatter={(v) => [fmtCurrency(v), "Revenue"]} />
                <Line type="monotone" dataKey="revenue" stroke={blue} strokeWidth={2} dot={false} isAnimationActive={false} />
              </LineChart>
            </ChartCard>

            <ChartCard title="Volume" subtitle="Approved vs declined per day">
              <BarChart data={series} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={cssVar("--border", 0.5)} />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={24} />
                <YAxis tick={{ fontSize: 11 }} width={44} tickFormatter={(v) => fmtInt(v)} />
                <Tooltip contentStyle={tooltipStyle()} formatter={(v, n) => [fmtInt(v), n === "approved" ? "Approved" : "Declined"]} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="approved" stackId="v" fill={brand} isAnimationActive={false} />
                <Bar dataKey="declined" stackId="v" fill={red} isAnimationActive={false} />
              </BarChart>
            </ChartCard>

            <ChartCard title="Average Transaction Value" subtitle="Daily revenue / transactions (USD)">
              <LineChart data={series} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={cssVar("--border", 0.5)} />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={24} />
                <YAxis tick={{ fontSize: 11 }} width={48} tickFormatter={(v) => `$${Math.round(v)}`} />
                <Tooltip contentStyle={tooltipStyle()} formatter={(v) => [fmtMoney2(v), "Avg Txn"]} />
                <Line type="monotone" dataKey="avg_txn_value" stroke={cssVar("--warning", 1) || "#f59e0b"} strokeWidth={2} dot={false} isAnimationActive={false} />
              </LineChart>
            </ChartCard>
          </div>
        )}

        {/* Company leaderboard (cross-company view only) */}
        {!loading && !companyId && byCompany.length > 0 && (
          <div className="surface rounded-xl ring-1 ring-border overflow-hidden">
            <div className="px-5 py-4 border-b border-border"><h3 className="text-sm font-semibold text-foreground">By Company</h3></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-card">
                  <tr className="text-left text-xs text-muted-foreground uppercase tracking-wider">
                    <th className="px-5 py-2">Company</th>
                    <th className="px-5 py-2 text-right">Transactions</th>
                    <th className="px-5 py-2 text-right">Approval</th>
                    <th className="px-5 py-2 text-right">Avg Txn</th>
                    <th className="px-5 py-2 text-right">Revenue</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {byCompany.map((c) => (
                    <tr key={c.company_id} className="hover:bg-muted/40 cursor-pointer" onClick={() => setCompanyId(c.company_id)}>
                      <td className="px-5 py-2 font-medium text-foreground">{c.company_id}</td>
                      <td className="px-5 py-2 text-right">{fmtInt(c.transactions)}</td>
                      <td className="px-5 py-2 text-right">{fmtPct(c.approval_rate)}</td>
                      <td className="px-5 py-2 text-right">{fmtMoney2(c.avg_txn_value)}</td>
                      <td className="px-5 py-2 text-right">{fmtCurrency(c.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Detail grid — all the rich per-merchant columns */}
        {!loading && rows.length > 0 && (
          <div className="surface rounded-xl ring-1 ring-border overflow-hidden">
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground">Detail (merchant × day)</h3>
              <span className="text-xs text-muted-foreground">{fmtInt(rows.length)} rows</span>
            </div>
            <div className="overflow-x-auto max-h-[600px]">
              <table className="w-full text-sm whitespace-nowrap">
                <thead className="bg-card sticky top-0">
                  <tr className="text-left text-xs text-muted-foreground uppercase tracking-wider">
                    <th className="px-3 py-2">Date</th>
                    <th className="px-3 py-2">Company</th>
                    <th className="px-3 py-2">Merchant</th>
                    <th className="px-3 py-2 text-right">Txns</th>
                    <th className="px-3 py-2 text-right">Appr</th>
                    <th className="px-3 py-2 text-right">Decl</th>
                    <th className="px-3 py-2 text-right">Appr %</th>
                    <th className="px-3 py-2 text-right">Appr % 7d</th>
                    <th className="px-3 py-2 text-right">Appr % 30d</th>
                    <th className="px-3 py-2 text-right">vs Day</th>
                    <th className="px-3 py-2 text-right">vs Week</th>
                    <th className="px-3 py-2 text-right">Revenue</th>
                    <th className="px-3 py-2 text-right">Rev 7d</th>
                    <th className="px-3 py-2 text-right">Avg Txn</th>
                    <th className="px-3 py-2 text-right">Rev vs Wk</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {rows.map((r, i) => (
                    <tr key={`${r.company_id}|${r.merchant_name}|${r.transaction_date}|${i}`} className="hover:bg-muted/40">
                      <td className="px-3 py-2 text-muted-foreground">{String(r.transaction_date).slice(0, 10)}</td>
                      <td className="px-3 py-2">{r.company_id}</td>
                      <td className="px-3 py-2 max-w-[260px] truncate" title={r.merchant_name}>{r.merchant_name}</td>
                      <td className="px-3 py-2 text-right">{fmtInt(r.transactions_today)}</td>
                      <td className="px-3 py-2 text-right">{fmtInt(r.approved_today)}</td>
                      <td className="px-3 py-2 text-right">{fmtInt(r.declined_today)}</td>
                      <td className="px-3 py-2 text-right font-medium">{fmtPct(r.approval_rate_today)}</td>
                      <td className="px-3 py-2 text-right text-muted-foreground">{fmtPct(r.approval_rate_7d)}</td>
                      <td className="px-3 py-2 text-right text-muted-foreground">{fmtPct(r.approval_rate_30d)}</td>
                      <td className={`px-3 py-2 text-right ${deltaClass(r.approval_rate_vs_prior_day)}`}>{fmtPP(r.approval_rate_vs_prior_day)}</td>
                      <td className={`px-3 py-2 text-right ${deltaClass(r.approval_rate_vs_prior_week)}`}>{fmtPP(r.approval_rate_vs_prior_week)}</td>
                      <td className="px-3 py-2 text-right">{fmtMoney2(r.revenue_today)}</td>
                      <td className="px-3 py-2 text-right text-muted-foreground">{fmtCurrency(r.revenue_7d)}</td>
                      <td className="px-3 py-2 text-right">{fmtMoney2(r.avg_txn_value_today)}</td>
                      <td className={`px-3 py-2 text-right ${deltaClass(r.revenue_vs_prior_week_pct)}`}>{fmtPctChange(r.revenue_vs_prior_week_pct)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!loading && !error && series.length === 0 && (
          <div className="surface rounded-xl ring-1 ring-border p-12 text-center">
            <BarChart3 className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">No data for the selected range.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default requireAuth(DailyPerformanceInner);
